/**
 * Shared primitives for the public marketing site (витрина).
 *
 * The site renders only for plain-browser visitors — App.tsx switches
 * on `tg.inTelegram`, so Telegram WebApp users never see it. Browser
 * sign-in (Telegram Login Widget / Google) is a later phase; until
 * then every CTA hands off to the Telegram bot.
 */
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

/**
 * Deep link straight into the Kit Pod Main Mini App (the
 * BotFather → @kitpod_bot → "Configure Mini App" → "Main App"
 * surface, URL = https://kitpod.ai/). Telegram opens the Main App
 * in one tap on mobile and desktop when the URL carries `?startapp`.
 *
 * Without `?startapp` the link drops the user into the bot chat
 * and they have to tap "Open App" — same UX as no deep link at all.
 *
 * To accept marketing-source tracking later, append a value
 * (`?startapp=ref_xyz`) — the WebApp receives it as `start_param`
 * on `initData`.
 */
export const BOT_URL = 'https://t.me/kitpod_bot?startapp';

/** Bot username (without @) whose Login Widget domain is whitelisted in
 * BotFather — used by the browser cabinet sign-in widget. Keep in sync
 * with the bot in {@link BOT_URL}. */
export const BOT_USERNAME = 'kitpod_bot';

/* ── Layout helpers ───────────────────────────────────────────── */

type AuroraVariant = 'default' | 'wide' | 'subtle';

/** Decorative drifting gradient blobs behind hero / CTA blocks. */
export function Aurora({ variant = 'default' }: { variant?: AuroraVariant }) {
  const blobs =
    variant === 'wide'
      ? [
          { w: 660, h: 660, top: -260, left: -200, c: 'var(--pb-accent)', delay: '0s', op: 0.45 },
          { w: 560, h: 560, top: -160, right: -180, c: 'var(--pb-accent-2)', delay: '-9s', op: 0.4 },
          { w: 480, h: 480, bottom: -160, left: '30%', c: 'var(--pb-accent)', delay: '-14s', op: 0.22 },
        ]
      : variant === 'subtle'
        ? [
            { w: 420, h: 420, top: -120, left: -100, c: 'var(--pb-accent)', delay: '0s', op: 0.3 },
            { w: 380, h: 380, top: -80, right: -100, c: 'var(--pb-accent-2)', delay: '-8s', op: 0.28 },
          ]
        : [
            { w: 540, h: 540, top: -220, left: -150, c: 'var(--pb-accent)', delay: '0s', op: 0.45 },
            { w: 460, h: 460, top: -130, right: -130, c: 'var(--pb-accent-2)', delay: '-8s', op: 0.42 },
          ];
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {blobs.map((b, i) => (
        <span
          key={i}
          className="site-aurora"
          style={{
            width: b.w,
            height: b.h,
            top: b.top,
            left: b.left,
            right: b.right,
            bottom: b.bottom,
            background: `radial-gradient(circle, ${b.c} 0%, transparent 68%)`,
            animationDelay: b.delay,
            opacity: b.op,
          }}
        />
      ))}
    </div>
  );
}

/** Faint dotted grid texture behind hero blocks — adds depth. */
export function DotGrid() {
  return <div aria-hidden className="pointer-events-none absolute inset-0 site-dotgrid" />;
}

/**
 * Staggered fade-and-rise entrance. Pure CSS — animation plays on
 * mount so content is never gated on JS.
 */
export function Reveal({
  children,
  className,
  delay,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <div
      className={clsx('site-reveal-in', className)}
      style={delay ? { animationDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}

/** Eyebrow + display heading + optional subtitle. */
export function SectionHeading({
  eyebrow,
  title,
  subtitle,
  center,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  center?: boolean;
}) {
  return (
    <div className={clsx('max-w-3xl', center && 'mx-auto text-center')}>
      <p className="pb-eyebrow text-pb-accent">{eyebrow}</p>
      <h2 className="pb-display mt-3 text-[clamp(28px,4vw,46px)] leading-[1.05]">
        {title}
      </h2>
      {subtitle ? (
        <p
          className={clsx(
            'mt-4 text-[16px] leading-relaxed text-tg-hint sm:text-[17px]',
            center ? 'mx-auto max-w-2xl' : 'max-w-2xl',
          )}
        >
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

/** Pill-style hero eyebrow chip with a dot. */
export function HeroEyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-pb-full border border-pb-accent-line bg-pb-paper/60 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-pb-accent backdrop-blur-md">
      <span className="h-1.5 w-1.5 rounded-full bg-pb-accent" />
      {children}
    </span>
  );
}

/* ── Product dashboard mockup (hero centerpiece) ──────────────── */

/** Bigger app-style mockup: top bar + stat column + chart + trades. */
export function ProductDashboardMock() {
  const { t } = useTranslation();
  return (
    <div className="relative rounded-pb-xl border border-white/[0.10] bg-white/[0.04] p-4 shadow-2xl backdrop-blur-2xl sm:p-5">
      <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-pb-sm bg-pb-accent-soft text-pb-accent">
            <KitGlyph />
          </span>
          <span className="text-xs font-semibold tracking-tight">Kit Pod</span>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-pb-success">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-pb-success" />
          {t('site.panel.online')}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-[1fr_1.6fr] gap-3 sm:gap-4">
        <div className="space-y-3">
          <SmallStat
            label={t('site.panel.today')}
            value="+$12.40"
            tone="success"
          />
          <SmallStat
            label={t('site.panel.total')}
            value="$1,240"
            tone="accent"
          />
          <div className="rounded-pb-md border border-white/[0.06] bg-pb-paper p-3">
            <p className="pb-eyebrow !text-tg-hint">{t('site.panel.bot')}</p>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs">{t('site.panel.active_status')}</span>
              <ToggleOnGlyph />
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <div className="rounded-pb-md border border-white/[0.06] bg-pb-paper p-3">
            <div className="flex items-center justify-between">
              <span className="pb-eyebrow !text-tg-hint">
                BTC · {t('site.panel.today')}
              </span>
              <span className="font-mono text-[11px] text-pb-success">
                +1.8%
              </span>
            </div>
            <div className="mt-2 h-20">
              <BtcChart />
            </div>
          </div>
          <div className="space-y-1.5">
            <MockTradeRow time="14:32" dir="up" amount="+$2.40" />
            <MockTradeRow time="14:18" dir="up" amount="+$1.10" />
            <MockTradeRow time="14:03" dir="down" amount="−$0.80" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Building blocks ──────────────────────────────────────────── */

function MockTradeRow({
  time,
  dir,
  amount,
}: {
  time: string;
  dir: 'up' | 'down';
  amount: string;
}) {
  return (
    <div className="flex items-center justify-between rounded-pb-sm bg-white/[0.02] px-3 py-2 text-[12px]">
      <div className="flex items-center gap-2">
        <span
          className={clsx(
            'flex h-5 w-5 items-center justify-center rounded-full text-[10px]',
            dir === 'up'
              ? 'bg-pb-success-soft text-pb-success'
              : 'bg-pb-danger/[0.15] text-pb-danger',
          )}
          aria-hidden
        >
          {dir === 'up' ? '↑' : '↓'}
        </span>
        <span className="font-mono text-tg-hint">{time}</span>
      </div>
      <span
        className={clsx(
          'pb-numeral font-medium',
          amount.startsWith('+') ? 'text-pb-success' : 'text-pb-danger',
        )}
      >
        {amount}
      </span>
    </div>
  );
}

function SmallStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'success' | 'accent';
}) {
  return (
    <div className="rounded-pb-md border border-white/[0.06] bg-pb-paper p-3">
      <p className="pb-eyebrow !text-tg-hint">{label}</p>
      <p
        className={clsx(
          'pb-numeral-lg mt-1 text-[20px] leading-none',
          tone === 'success' && 'text-pb-success',
          tone === 'accent' && 'text-pb-accent',
        )}
      >
        {value}
      </p>
    </div>
  );
}

function ToggleOnGlyph() {
  return (
    <span
      aria-hidden
      className="inline-flex h-4 w-7 items-center rounded-full bg-pb-success p-0.5"
    >
      <span className="ml-auto h-3 w-3 rounded-full bg-white" />
    </span>
  );
}

function KitGlyph() {
  return (
    <svg width={12} height={12} viewBox="0 0 12 12" fill="none" aria-hidden>
      <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="6" cy="6" r="1.5" fill="currentColor" />
    </svg>
  );
}

/** Stylized rising-trend chart with a pulsing endpoint. */
export function BtcChart() {
  return (
    <svg
      viewBox="0 0 220 80"
      preserveAspectRatio="none"
      className="h-full w-full"
      aria-hidden
    >
      <defs>
        <linearGradient id="btc-chart-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--pb-accent)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--pb-accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M0 62 L20 56 L36 60 L54 48 L72 52 L92 40 L112 44 L132 32 L156 36 L178 24 L200 28 L220 16 L220 80 L0 80 Z"
        fill="url(#btc-chart-fill)"
      />
      <path
        d="M0 62 L20 56 L36 60 L54 48 L72 52 L92 40 L112 44 L132 32 L156 36 L178 24 L200 28 L220 16"
        fill="none"
        stroke="var(--pb-accent)"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="220" cy="16" r="3" fill="var(--pb-accent)" />
      <circle
        cx="220"
        cy="16"
        r="8"
        fill="var(--pb-accent)"
        opacity="0.22"
        className="site-endpoint-pulse"
      />
    </svg>
  );
}

